import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import { Link, useNavigate } from 'react-router-dom';
import { FiChevronRight, FiEdit2, FiTrash2, FiEye } from "react-icons/fi";
import { BaseInput, BaseSelect, BaseButton } from "../../components/ui";
import gastoService from '../../services/gastoService';
import useCanDelete from '../../hooks/useCanDelete';

const ESTADO_OPTIONS = [
  { value: 'TODOS', label: 'Todos' },
  { value: 'PENDIENTE', label: 'Pendiente' },
  { value: 'PAGADO', label: 'Pagado' },
  { value: 'CANCELADO', label: 'Cancelado' },
];

const GastosCrud = () => {
  const navigate = useNavigate();
  const canDelete = useCanDelete();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('TODOS');
  const [filtroFecha, setFiltroFecha] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('TODAS');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const gastosData = await gastoService.obtenerGastos();
        setGastos(gastosData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Error al cargar los gastos');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    if (name === 'estado') setFiltroEstado(value);
    if (name === 'categoria') setFiltroCategoria(value);
    if (name === 'fecha') setFiltroFecha(value);
  };

  const handleEliminarGasto = async (id) => {
    if (window.confirm('¿Está seguro que desea eliminar este gasto?')) {
      try {
        await gastoService.eliminarGasto(id);
        setGastos(gastos.filter(gasto => gasto.id !== id));
        alert('Gasto eliminado exitosamente');
      } catch (error) {
        console.error('Error al eliminar el gasto:', error);
        alert('Error al eliminar el gasto');
      }
    }
  };

  const categoriasGasto = [
    'TODAS',
    'MANTENIMIENTO',
    'SERVICIOS',
    'REPARACIONES',
    'LIMPIEZA',
    'SEGURIDAD',
    'ADMINISTRATIVO',
    'OTROS'
  ];

  const gastosFiltrados = gastos.filter(gasto => {
    const cumpleEstado = filtroEstado === 'TODOS' || gasto.estado === filtroEstado;
    const cumpleFecha = !filtroFecha || gasto.fechaGasto.includes(filtroFecha);
    const cumpleCategoria = filtroCategoria === 'TODAS' || gasto.categoria === filtroCategoria;
    return cumpleEstado && cumpleFecha && cumpleCategoria;
  });

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <Sidebar id='menu-item6' id1='menu-items6' activeClassName='contabilidad-gastos'/>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Contabilidad</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FiChevronRight icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Gestión de Gastos</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-heading">
                        <h4>Gestión de Gastos</h4>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-xl-3">
                      <BaseSelect
                        native
                        label="Filtrar por Estado"
                        name="estado"
                        value={filtroEstado}
                        onChange={handleFiltroChange}
                        options={ESTADO_OPTIONS}
                      />
                    </div>
                    <div className="col-12 col-md-6 col-xl-3">
                      <BaseSelect
                        native
                        label="Filtrar por Categoría"
                        name="categoria"
                        value={filtroCategoria}
                        onChange={handleFiltroChange}
                        options={categoriasGasto.map((categoria) => ({
                          value: categoria,
                          label: categoria === 'TODAS' ? 'Todas' : categoria,
                        }))}
                      />
                    </div>
                    <div className="col-12 col-md-6 col-xl-3">
                      <BaseInput
                        label="Filtrar por Fecha"
                        name="fecha"
                        type="month"
                        value={filtroFecha}
                        onChange={handleFiltroChange}
                      />
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Inmueble</th>
                          <th>Concepto</th>
                          <th>Monto</th>
                          <th>Fecha del Gasto</th>
                          <th>Categoría</th>
                          <th>Estado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gastosFiltrados.map((gasto) => (
                          <tr key={gasto.id}>
                            <td>{gasto.id}</td>
                            <td>{gasto.inmuebleNombre}</td>
                            <td>{gasto.concepto}</td>
                            <td>${gasto.monto.toFixed(2)}</td>
                            <td>{gasto.fechaGasto}</td>
                            <td>{gasto.categoria}</td>
                            <td>
                              <span className={`badge bg-${gasto.estado === 'PAGADO' ? 'success' : gasto.estado === 'PENDIENTE' ? 'warning' : 'danger'}`}>
                                {gasto.estado}
                              </span>
                            </td>
                            <td>
                              <div className="actions">
                                <BaseButton
                                  type="button"
                                  variant="success"
                                  size="sm"
                                  className="me-2"
                                  icon={<FiEye />}
                                  onClick={() => navigate(`/contabilidad/gastos/ver/${gasto.id}`)}
                                  title="Ver"
                                />
                                <BaseButton
                                  type="button"
                                  variant="primary"
                                  size="sm"
                                  className="me-2"
                                  icon={<FiEdit2 />}
                                  onClick={() => navigate(`/contabilidad/gastos/editar/${gasto.id}`)}
                                  title="Editar"
                                />
                                {canDelete && (
                                  <BaseButton
                                    type="button"
                                    variant="danger"
                                    size="sm"
                                    icon={<FiTrash2 />}
                                    onClick={() => handleEliminarGasto(gasto.id)}
                                    title="Eliminar"
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GastosCrud;
